import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../../data/supabaseClient';
import { FaRegHandshake, FaRegThumbsUp, FaRegStar, FaCog, FaCarSide, FaCheckCircle, FaTimes, FaEllipsisV, FaMapMarkerAlt, FaBars, FaCaretDown, FaClock, FaExclamationTriangle, FaUsers, FaHandshake } from "react-icons/fa";
import { CustomButton10, CustomButton10Ca, CustomButton10Co } from '../../Templates';
import { useToast } from '../../hooks/ToastContext';
import { useTranslation } from 'react-i18next';
import Spinner from '../../common/Spinner';
import './RideActionsV2.css';

// Import V2 Phase Components
import MeetingAgreement from './MeetingAgreement';
import ArrivalConfirmation from './ArrivalConfirmation';
import WaitingStatus from './WaitingStatus';
import TimerExtension from './TimerExtension';
import NoShowDetection from './NoShowDetection';
import ReadyConfirmation from './ReadyConfirmation';
import StatusDisplay from './StatusDisplay';

// V2 Status Constants
const RideStatusV2 = {
  INITIAL: 'initial',
  REQUEST_SENT: 'request_sent',
  CONFIRMED: 'confirmed',
  MEETING_AGREEMENT: 'meeting_agreement',
  MEETING_AGREED: 'meeting_agreed',
  ARRIVAL_PHASE: 'arrival_phase',
  WAITING_FOR_OTHER: 'waiting_for_other',
  BOTH_PRESENT: 'both_present',
  READY_TO_START: 'ready_to_start',
  IN_PROGRESS: 'in_progress',
  COMPLETION_DRIVER: 'completion_driver',
  COMPLETION_PASSENGER: 'completion_passenger',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  DECLINED: 'declined',
  PASSENGER_NO_SHOW: 'passenger_no_show',
  DRIVER_NO_SHOW: 'driver_no_show',
  MUTUAL_NO_SHOW: 'mutual_no_show',
  MEETING_DISAGREEMENT: 'meeting_disagreement',
  PRESENCE_DISPUTED: 'presence_disputed'
};

const RideActionsV2 = ({ conversationId, postId }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  
  // Core state
  const [currentUser, setCurrentUser] = useState(null);
  const [post, setPost] = useState(null);
  const [ride, setRide] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // V2 specific state
  const [meetingTimeProposal, setMeetingTimeProposal] = useState(null);
  const [waitingDuration, setWaitingDuration] = useState(3); // Default 3 minutes
  const [timerExtensionRequest, setTimerExtensionRequest] = useState(null);
  const [userArrivalStatus, setUserArrivalStatus] = useState({
    driver: null,
    passenger: null
  });
  
  // Review state
  const [driverReviewSubmitted, setDriverReviewSubmitted] = useState(false);
  const [passengerReviewSubmitted, setPassengerReviewSubmitted] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  
  // Simplified toggle handler
  const handleToggleDropdown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    setDropdownOpen(prev => !prev);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only handle if dropdown is open
      if (!dropdownOpen) return;
      
      // Check if any modal is open
      const modalExists = document.querySelector('.RAC-modal-overlay-v2, .RAC-modal-overlay, .modal-overlay, .review-dialog-container');
      if (modalExists) return;
      
      // Check if click is outside dropdown AND not on the toggle button
      if (dropdownRef.current && 
          !dropdownRef.current.contains(event.target) &&
          !event.target.closest('.RAC-ride-actions-button')) {
        setDropdownOpen(false);
      }
    };
    
    // Use only mousedown to avoid event conflicts
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]); // Add dropdownOpen as dependency

  // Initialize data and subscriptions with retry logic
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 3;
    
    const initializeData = async (attempt = 1) => {
      if (!postId || !conversationId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        console.log(`Initializing ride actions data (attempt ${attempt})...`);
        
        // Get current user
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        setCurrentUser(user);

        // Get post details
        const { data: postData, error: postError } = await supabase
          .from('posts')
          .select('*')
          .eq('id', postId)
          .single();
        
        if (postError) throw postError;
        setPost(postData);

        // Get existing ride for this specific conversation
        const { data: rideData, error: rideError } = await supabase
          .from('rides')
          .select('*')
          .eq('post_id', postId)
          .eq('conversation_id', conversationId) // Filter by conversation
          .not('status', 'eq', 'cancelled')
          .not('status', 'eq', 'declined')
          .maybeSingle();

        if (rideError && rideError.code !== 'PGRST116') throw rideError;
        
        console.log('Ride data fetched:', rideData);
        setRide(rideData || null);

        // Get other user details if ride exists
        if (rideData && user) {
          const otherUserId = user.id === rideData.driver_id ? 
            rideData.passenger_id : rideData.driver_id;
          
          const { data: otherUserData, error: otherUserError } = await supabase
            .from('profiles')
            .select('id, username, avatar_url')
            .eq('id', otherUserId)
            .single();
          
          if (otherUserError) {
            console.error('Error fetching other user:', otherUserError);
          } else {
            setOtherUser(otherUserData);
          }
            
          // If ride is completed, check review status
          if (rideData.status === RideStatusV2.COMPLETED) {
            await loadReviewStatus(rideData);
          }
        }
        
        console.log('Ride actions initialization complete');

      } catch (error) {
        console.error(`Error initializing V2 data (attempt ${attempt}):`, error);
        
        // Retry logic for network errors
        if (attempt < maxRetries && (error.message?.includes('network') || error.message?.includes('fetch'))) {
          console.log(`Retrying initialization in 2 seconds... (${attempt}/${maxRetries})`);
          setTimeout(() => initializeData(attempt + 1), 2000);
          return;
        }
        
        setError(t('ride_actions.error_loading'));
      } finally {
        setLoading(false);
      }
    };

    // Start the initialization
    initializeData();

    // Set up real-time subscriptions for V2 with better error handling
    const rideUpdateSubscription = supabase
      .channel(`ride-v2-update-${conversationId}`)
      .on('postgres_changes', 
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'rides',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          console.log('V2 Ride updated:', payload.new);
          if (payload.new && payload.new.conversation_id === conversationId) {
            // Always update with the latest data to catch extension_requests changes
            setRide(payload.new);
            
            // If status changed to completed, check reviews
            if (payload.new.status === RideStatusV2.COMPLETED) {
              loadReviewStatus(payload.new);
            }
          }
        }
      )
      .subscribe();
      
    const rideInsertSubscription = supabase
      .channel(`ride-v2-insert-${conversationId}`)
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'rides',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          console.log('V2 New ride created:', payload.new);
          if (payload.new && payload.new.conversation_id === conversationId) {
            // Only set if we don't already have a ride or this is newer
            setRide(prevRide => {
              if (!prevRide || (payload.new.created_at && new Date(payload.new.created_at) >= new Date(prevRide.created_at))) {
                return payload.new;
              }
              return prevRide;
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(rideUpdateSubscription);
      supabase.removeChannel(rideInsertSubscription);
    };
  }, [postId, conversationId, t]);

  // Load review status for completed rides
  const loadReviewStatus = async (rideData) => {
    try {
      if (!rideData?.id) return;

      const driverReview = await checkExistingReview(rideData.id, rideData.driver_id);
      const passengerReview = await checkExistingReview(rideData.id, rideData.passenger_id);

      setDriverReviewSubmitted(!!driverReview);
      setPassengerReviewSubmitted(!!passengerReview);
    } catch (error) {
      console.error('Error loading review status:', error);
    }
  };

  // Check if a review already exists
  const checkExistingReview = async (rideId, reviewerId) => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('ride_id', rideId)
        .eq('reviewer_id', reviewerId)
        .maybeSingle();
  
      if (error && error.code !== 'PGRST116') {
        console.error('Error checking review:', error);
        return false;
      }
  
      return !!data;
    } catch (error) {
      console.error('Error in checkExistingReview:', error);
      return false;
    }
  };

  // Create wrapper function that matches the old signature for child components
  const createNotification = async (userId, type, message, relatedId = null) => {
    // Note: message parameter is ignored - we use database translation instead
    try {
      const { error: notificationError } = await supabase.rpc('create_translated_notification', {
        target_user_id: userId,
        notification_type: type,
        sender_id: currentUser.id,
        conversation_id: conversationId,
        related_id: relatedId ? relatedId.toString() : null
      });
      
      if (notificationError) {
        console.error('Failed to create translated notification:', notificationError);
        throw notificationError;
      }
    } catch (error) {
      console.error('Error creating translated notification:', error);
      throw error;
    }
  };

  // Function to add system message about ride actions
  const addSystemMessage = async (messageText) => {
    try {
      if (!conversationId || !messageText) return;
      
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('Error getting current user for system message:', userError);
        return;
      }
      
      if (!currentUser?.id) {
        console.error('No current user ID available for system message');
        return;
      }
      
      await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          user_id: currentUser.id,
          text: messageText,
          created_at: new Date().toISOString(),
          is_system_message: true
        });
      
      await supabase
        .from('conversations')
        .update({
          last_message: messageText,
          last_updated: new Date().toISOString()
        })
        .eq('id', conversationId);
        
    } catch (error) {
      console.error('Error adding system message:', error);
    }
  };

  // Handle ride completion
  const handleRideComplete = async () => {
    setDropdownOpen(false);
    if (actionLoading) return;
    
    try {
      setActionLoading(true);
      setError(null);
      
      if (!ride?.id) {
        throw new Error(t('ride_actions.error_no_ride'));
      }

      if (!currentUser) {
        throw new Error(t('ride_actions.error_not_logged_in'));
      }
      
      const isUserDriver = currentUser.id === ride.driver_id;
      const otherUserId = isUserDriver ? ride.passenger_id : ride.driver_id;

      // Determine new status based on current status and who is completing
      let finalStatus;
      
      if (ride.status === RideStatusV2.IN_PROGRESS) {
        // If ride is in progress, move to completion phase
        finalStatus = isUserDriver ? 
          RideStatusV2.COMPLETION_DRIVER : 
          RideStatusV2.COMPLETION_PASSENGER;
      } else if (ride.status === RideStatusV2.COMPLETION_DRIVER || ride.status === RideStatusV2.COMPLETION_PASSENGER) {
        // One party already marked complete
        // Check if the current user is trying to complete and they haven't already
        if (
          (isUserDriver && ride.status === RideStatusV2.COMPLETION_DRIVER) ||
          (!isUserDriver && ride.status === RideStatusV2.COMPLETION_PASSENGER)
        ) {
          // User already marked their completion
          throw new Error(t('ride_actions.already_marked_complete'));
        }
        
        // The other party is completing, so mark as fully completed
        finalStatus = RideStatusV2.COMPLETED;
      } else {
        throw new Error(t('ride_actions.invalid_status_completion'));
      }

      // Update ride status
      const { error: rideError } = await supabase
        .from('rides')
        .update({ 
          status: finalStatus,
          ...(finalStatus === RideStatusV2.COMPLETED ? { completed_at: new Date().toISOString() } : {}),
          updated_at: new Date().toISOString()
        })
        .eq('id', ride.id);

      if (rideError) throw rideError;

      // Manual stats update as backup (in case triggers don't work)
      if (finalStatus === RideStatusV2.COMPLETED) {
        try {
          console.log('Manually updating user stats for completed ride');
          
          // Get the distance from post if not in ride
          let rideDistance = ride.distance_km;
          if (!rideDistance && post?.distance_km) {
            rideDistance = post.distance_km;
          }
          
          // Update driver stats
          const { error: driverStatsError } = await supabase
            .rpc('sync_user_stats_for_user', { p_user_id: ride.driver_id });
          
          if (driverStatsError) {
            console.error('Error updating driver stats:', driverStatsError);
          }
          
          // Update passenger stats
          const { error: passengerStatsError } = await supabase
            .rpc('sync_user_stats_for_user', { p_user_id: ride.passenger_id });
          
          if (passengerStatsError) {
            console.error('Error updating passenger stats:', passengerStatsError);
          }
          
          console.log('Manual stats update completed');
          
        } catch (statsError) {
          console.error('Error in manual stats update:', statsError);
          // Don't throw here, ride completion is more important than stats
        }
      }

      // Create notification using database translation
      try {
        const { error: notificationError } = await supabase.rpc('create_translated_notification', {
          target_user_id: otherUserId,
          notification_type: finalStatus === RideStatusV2.COMPLETED ? 'ride_completed' : 'ride_status_change',
          sender_id: currentUser.id,
          conversation_id: conversationId,
          related_id: ride.id.toString()
        });
        
        if (notificationError) {
          console.error('Failed to create ride completion notification:', notificationError);
        }
      } catch (notificationError) {
        console.error('Error creating ride completion notification:', notificationError);
      }
      
      // Add system message
      await addSystemMessage(
        finalStatus === RideStatusV2.COMPLETED ?
          t('ride_actions.journey_completed_system') :
          t('ride_actions.marked_complete_system', { role: isUserDriver ? t('ride_actions.driver') : t('ride_actions.passenger') })
      );

      addToast({
        type: finalStatus === RideStatusV2.COMPLETED ? 'success' : 'info',
        title: finalStatus === RideStatusV2.COMPLETED ? t('ride_actions.journey_completed') : t('ride_actions.marked_complete'),
        description: finalStatus === RideStatusV2.COMPLETED ?
          t('ride_actions.journey_completed_notification') :
          t('ride_actions.waiting_for_other_confirm', { role: isUserDriver ? t('ride_actions.passenger') : t('ride_actions.driver') })
      });

    } catch (error) {
      console.error('Error completing ride:', error);
      setError(t('ride_actions.error_completion'));
      
      addToast({
        type: 'error',
        title: t('ride_actions.error'),
        description: error.message || t('ride_actions.error_completion')
      });
    } finally {
      setActionLoading(false);
    }
  };



  // Handle initial ride request
  const handleRequestRide = async () => {
    setDropdownOpen(false);
    if (actionLoading) return;
    
    try {
      setActionLoading(true);
      setError(null);
      
      if (!currentUser || !post) {
        throw new Error(t('ride_actions.error_user_or_post'));
      }
  
      // Determine roles correctly
      const isDriverPost = post.role === 'Driver';
      const driverId = isDriverPost ? post.user_id : currentUser.id;
      const passengerId = isDriverPost ? currentUser.id : post.user_id;
      
      // Create the ride with V2 initial status
      const { data: newRide, error: rideError } = await supabase
        .from('rides')
        .insert([{
          post_id: postId,
          passenger_id: passengerId,
          driver_id: driverId,
          conversation_id: conversationId, // Link to specific conversation
          status: RideStatusV2.REQUEST_SENT,
          created_at: new Date().toISOString(),
          // V2 specific fields
          meeting_duration_minutes: waitingDuration,
          v2_enabled: true // Flag to identify V2 rides
        }])
        .select()
        .single();
  
      if (rideError) throw rideError;
  
      // Create notification using database translation
      try {
      const { error: notificationError } = await supabase.rpc('create_translated_notification', {
        target_user_id: post.user_id,
        notification_type: 'ride_request',
        sender_id: currentUser.id,
          conversation_id: conversationId,
      related_id: newRide.id.toString()
    });
    
    if (notificationError) {
      console.error('Failed to create ride request notification:', notificationError);
    }
  } catch (notificationError) {
    console.error('Error creating ride request notification:', notificationError);
  }
      
      // Add system message
      await addSystemMessage(t('ride_actions.system_message_request_sent'));
  
      addToast({
        type: 'success',
        title: t('ride_actions.success'),
        description: t('ride_actions.request_sent')
      });
  
    } catch (error) {
      console.error('Error in handleRequestRide:', error);
      setError(t('ride_actions.error_request'));
      
      addToast({
        type: 'error',
        title: t('ride_actions.error'),
        description: error.message || t('ride_actions.error_request')
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle accepting/declining requests
  const handleRequestResponse = async (accept) => {
    setDropdownOpen(false);
    if (actionLoading) return;
    
    try {
      setActionLoading(true);
      setError(null);
      
      if (!ride?.id) {
        throw new Error(t('ride_actions.error_no_ride'));
      }

      // Update ride status
      const newStatus = accept ? RideStatusV2.CONFIRMED : RideStatusV2.DECLINED;
      const { error: rideError } = await supabase
        .from('rides')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', ride.id);

      if (rideError) throw rideError;

      // Get recipient ID
      const recipientId = currentUser.id === ride.driver_id ? 
        ride.passenger_id : ride.driver_id;

      // Create notification using database translation
      try {
        const { error: notificationError } = await supabase.rpc('create_translated_notification', {
          target_user_id: recipientId,
          notification_type: accept ? 'ride_accepted' : 'ride_declined',
          sender_id: currentUser.id,
          conversation_id: conversationId,
          related_id: ride.id.toString()
        });
        
        if (notificationError) {
          console.error('Failed to create ride response notification:', notificationError);
        }
      } catch (notificationError) {
        console.error('Error creating ride response notification:', notificationError);
      }
      
      // Add system message
      await addSystemMessage(accept ? 
        t('ride_actions.request_accepted_message') : 
        t('ride_actions.request_declined_message'));

      addToast({
        type: accept ? 'success' : 'info',
        title: accept ? t('ride_actions.accepted') : t('ride_actions.declined'),
        description: accept ? 
          t('ride_actions.request_was_accepted') : 
          t('ride_actions.request_was_declined')
      });

    } catch (error) {
      console.error('Error responding to request:', error);
      setError(t('ride_actions.error_response'));
      
      addToast({
        type: 'error',
        title: t('ride_actions.error'),
        description: error.message || t('ride_actions.error_response')
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Determine user roles
  const getUserRole = () => {
    if (!currentUser || !post) return null;
    
    // If this is a driver post and current user is the post creator, they're the driver
    if (post.role === 'Driver' && currentUser.id === post.user_id) {
      return t('ride_actions.driver');
    }
    // If this is a passenger post and current user is the post creator, they're the passenger
    if (post.role === 'Passenger' && currentUser.id === post.user_id) {
      return t('ride_actions.passenger');
    }
    // If current user is not the post creator, they take the opposite role
    return post.role === 'Driver' ? t('ride_actions.passenger') : t('ride_actions.driver');
  };

  const getOtherUserRole = () => {
    const userRole = getUserRole();
    return userRole === t('ride_actions.driver') ? t('ride_actions.passenger') : t('ride_actions.driver');
  };

  const isPostCreator = currentUser?.id === post?.user_id;
  const userRole = getUserRole();
  const otherUserRole = getOtherUserRole();

  // Get status info for display
  const getStatusInfo = (status) => {
    let icon = <FaCarSide />;
    let color = 'var(--main-color)';
    let needsAction = false;
    let label = '';
    
    switch (status) {
      case RideStatusV2.REQUEST_SENT:
        label = t('ride_actions.request_sent');
        color = 'var(--accent-color)';
        icon = <FaRegHandshake />;
        needsAction = isPostCreator;
        break;
      case RideStatusV2.CONFIRMED:
        label = t('ride_actions.confirmed'); 
        color = 'var(--accent-color)';
        icon = <FaCheckCircle />;
        needsAction = true; // Both users need to set meeting time
        break;
      case RideStatusV2.MEETING_AGREEMENT:
        label = t('ride_actions.setting_meeting_time');
        color = '#F59E0B';
        icon = <FaClock />;
        needsAction = true;
        break;
      case RideStatusV2.MEETING_AGREED:
        label = t('ride_actions.meeting_agreed');
        color = 'var(--accent-color)';
        icon = <FaHandshake />;
        needsAction = true; // Can confirm arrival
        break;
      case RideStatusV2.ARRIVAL_PHASE:
        label = t('ride_actions.arrival_phase');
        color = '#F59E0B';
        icon = <FaMapMarkerAlt />;
        needsAction = true;
        break;
      case RideStatusV2.WAITING_FOR_OTHER:
        label = t('ride_actions.waiting_for_other');
        color = '#F59E0B';
        icon = <FaClock />;
        needsAction = false; // Waiting state
        break;
      case RideStatusV2.BOTH_PRESENT:
        label = t('ride_actions.both_present');
        color = 'var(--accent-color)';
        icon = <FaUsers />;
        needsAction = true; // Can confirm ready
        break;
      case RideStatusV2.READY_TO_START:
        label = t('ride_actions.ready_to_start');
        color = 'var(--accent-color)';
        icon = <FaCheckCircle />;
        needsAction = true;
        break;
      case RideStatusV2.IN_PROGRESS:
        label = t('ride_actions.in_progress');
        color = '#3B82F6';
        icon = <FaCarSide />;
        break;
      case RideStatusV2.COMPLETION_DRIVER:
        label = t('ride_actions.driver_completed');
        needsAction = currentUser?.id === ride?.passenger_id;
        color = 'var(--main-color)';
        icon = <FaCheckCircle />;
        break;
      case RideStatusV2.COMPLETION_PASSENGER:
        label = t('ride_actions.passenger_completed');
        needsAction = currentUser?.id === ride?.driver_id;
        color = 'var(--main-color)';
        icon = <FaCheckCircle />;
        break;
      case RideStatusV2.COMPLETED:
        label = t('ride_actions.completed');
        color = 'var(--accent-color)';
        icon = <FaCheckCircle />;
        break;
      case RideStatusV2.CANCELLED:
        label = t('ride_actions.cancelled');
        color = 'var(--light-color)';
        icon = <FaTimes />;
        break;
      case RideStatusV2.DECLINED:
        label = t('ride_actions.declined');
        color = 'var(--light-color)';
        icon = <FaTimes />;
        break;
      case RideStatusV2.PASSENGER_NO_SHOW:
        label = t('ride_actions.passenger_no_show_status');
        color = '#EF4444';
        icon = <FaExclamationTriangle />;
        break;
      case RideStatusV2.DRIVER_NO_SHOW:
        label = t('ride_actions.driver_no_show_status');
        color = '#EF4444';
        icon = <FaExclamationTriangle />;
        break;
      case RideStatusV2.PRESENCE_DISPUTED:
        label = t('ride_actions.presence_disputed');
        color = '#F59E0B';
        icon = <FaExclamationTriangle />;
        needsAction = true;
        break;
      case RideStatusV2.CANCELLED:
        // Check cancellation reason for more specific labels
        if (ride?.cancellation_reason === 'presence_dispute_admitted') {
          label = t('ride_actions.cancelled_no_show');
        } else if (ride?.cancellation_reason === 'presence_dispute_evidence') {
          label = t('ride_actions.cancelled_dispute');
        } else {
          label = t('ride_actions.cancelled');
        }
        color = 'var(--light-color)';
        icon = <FaTimes />;
        break;
      default:
        label = userRole || t('posts.ride');
    }
    
    return { label, icon, color, needsAction };
  };

  // Get compact status label for button display
  const getCompactLabel = () => {
    // Show loading indicator while fetching data
    if (loading) return t('app.loading');
    
    // Show actual status if ride exists
    if (ride) {
      const statusInfo = getStatusInfo(ride.status);
      return statusInfo.label;
    }
    
    // Default fallback only after loading is complete
    return userRole || t('posts.ride');
  };

  // Render phase-specific components
  const renderPhaseComponent = () => {
    if (!ride) return null;

    switch (ride.status) {
      case RideStatusV2.CONFIRMED:
      case RideStatusV2.MEETING_AGREEMENT:
        return (
          <MeetingAgreement
            ride={ride}
            currentUser={currentUser}
            otherUser={otherUser}
            userRole={userRole}
            post={post}
            onUpdate={setRide}
            addSystemMessage={addSystemMessage}
            createNotification={createNotification}
          />
        );
      
      case RideStatusV2.MEETING_AGREED:
        return (
          <ArrivalConfirmation
            ride={ride}
            currentUser={currentUser}
            otherUser={otherUser}
            userRole={userRole}
            onUpdate={setRide}
            addSystemMessage={addSystemMessage}
            createNotification={createNotification}
          />
        );
      
      case RideStatusV2.ARRIVAL_PHASE:
        // If timer not started yet, still show arrival confirmation
        if (!ride.timer_started_at) {
          return (
            <ArrivalConfirmation
              key={`arrival-${ride.id}-${ride.updated_at}`}
              ride={ride}
              currentUser={currentUser}
              otherUser={otherUser}
              userRole={userRole}
              onUpdate={setRide}
              addSystemMessage={addSystemMessage}
              createNotification={createNotification}
            />
          );
        }
        // Timer started, show waiting status
        return (
          <WaitingStatus
            key={`waiting-${ride.id}-${ride.updated_at}-${JSON.stringify(ride.extension_requests || [])}`}
            ride={ride}
            currentUser={currentUser}
            otherUser={otherUser}
            userRole={userRole}
            onUpdate={setRide}
            addSystemMessage={addSystemMessage}
            createNotification={createNotification}
          />
        );
      
      case RideStatusV2.WAITING_FOR_OTHER:
        return (
          <WaitingStatus
            key={`waiting-${ride.id}-${ride.updated_at}-${JSON.stringify(ride.extension_requests || [])}`}
            ride={ride}
            currentUser={currentUser}
            otherUser={otherUser}
            userRole={userRole}
            onUpdate={setRide}
            addSystemMessage={addSystemMessage}
            createNotification={createNotification}
          />
        );
      
      case RideStatusV2.BOTH_PRESENT:
      case RideStatusV2.READY_TO_START:
      case RideStatusV2.PRESENCE_DISPUTED:
        return (
          <ReadyConfirmation
            ride={ride}
            currentUser={currentUser}
            otherUser={otherUser}
            userRole={userRole}
            onUpdate={setRide}
            addSystemMessage={addSystemMessage}
            createNotification={createNotification}
          />
        );
      case RideStatusV2.IN_PROGRESS:
        return (
          <div className="RAC-in-progress-section">
            <div style={{ fontSize: '11px', textAlign: 'center', marginBottom: '6px' }}>
              {t('ride_actions.journey_in_progress')}
            </div>
            <CustomButton10Co
              onClick={handleRideComplete}
              label={t('ride_actions.complete_journey')}
              disabled={actionLoading}
            >
              {actionLoading && <Spinner size="small" showText={false} thickness={2} />}
            </CustomButton10Co>
          </div>
        );
      
      case RideStatusV2.COMPLETION_DRIVER:
        if (currentUser?.id === ride.driver_id) {
          return <div style={{ fontSize: '11px', textAlign: 'center' }}>{t('ride_actions.waiting_for_passenger_complete')}</div>;
        }
        if (currentUser?.id === ride.passenger_id) {
          return (
            <CustomButton10Co
              onClick={handleRideComplete}
              label={t('ride_actions.complete_journey')}
              disabled={actionLoading}
            >
              {actionLoading && <Spinner size="small" showText={false} thickness={2} />}
            </CustomButton10Co>
          );
        }
        break;
  
      case RideStatusV2.COMPLETION_PASSENGER:
        if (currentUser?.id === ride.passenger_id) {
          return <div style={{ fontSize: '11px', textAlign: 'center' }}>{t('ride_actions.waiting_for_driver_complete')}</div>;
        }
        if (currentUser?.id === ride.driver_id) {
          return (
            <CustomButton10Co
              onClick={handleRideComplete}
              label={t('ride_actions.complete_journey')}
              disabled={actionLoading}
            >
              {actionLoading && <Spinner size="small" showText={false} thickness={2} />}
            </CustomButton10Co>
          );
        }
        break;
        
      default:
        return null;
    }
  };

  // Render appropriate actions based on status and roles
  const renderActions = () => {
    // Show loading state while data is being fetched
    if (loading) {
      return (
        <div className="RAC-loading">
          <Spinner size="small" showText={false} />
        </div>
      );
    }

    if (error) {
      return (
        <div className="RAC-error">
          <p>{error}</p>
        </div>
      );
    }

    // Handle completed rides
    if (ride?.status === RideStatusV2.COMPLETED) {
      const isDriver = currentUser?.id === ride.driver_id;
      const canReview = isDriver ? !driverReviewSubmitted : !passengerReviewSubmitted;
      
      return (
        <div className="RAC-action-buttons">
          <div style={{ fontSize: '11px' }}>{t('ride_actions.journey_completed')}</div>
          {canReview && (
            <CustomButton10Co
              onClick={() => setShowReviewDialog(true)}
              label={t('ride_actions.leave_review')}
              disabled={actionLoading}
            >
              <FaRegStar />
            </CustomButton10Co>
          )}
        </div>
      );
    }
    
    // Handle cancelled rides
    if (ride?.status === RideStatusV2.CANCELLED) {
      let cancellationMessage = t('ride_actions.cancelled');
      
      if (ride.cancellation_reason === 'presence_dispute_admitted') {
        cancellationMessage = t('ride_actions.ride_cancelled_no_show');
      } else if (ride.cancellation_reason === 'presence_dispute_evidence') {
        cancellationMessage = t('ride_actions.ride_cancelled_dispute');
      }
      
      return (
        <div style={{ fontSize: '11px', textAlign: 'center', color: 'var(--light-color)' }}>
          {cancellationMessage}
        </div>
      );
    }
    
    // Handle declined rides
    if (ride?.status === RideStatusV2.DECLINED) {
      return (
        <div style={{ fontSize: '11px', textAlign: 'center', color: 'var(--light-color)' }}>
          {t('ride_actions.declined')}
        </div>
      );
    }
  
    // Handle no ride scenario - ONLY after loading is complete
    if (!ride && !loading) {
      if (isPostCreator) {
        return <div style={{ fontSize: '11px', textAlign: 'center' }}>{t(userRole === t('ride_actions.driver') ? 'ride_actions.ask_passengers' : 'ride_actions.ask_drivers')}</div>;
      }
      
      return (
        <CustomButton10
          onClick={handleRequestRide}
          label={post?.role === 'Driver' ? t('buttons.request') : t('ride_actions.offer_ride')}
          disabled={actionLoading}
        >
          {actionLoading ? <Spinner size="small" showText={false} thickness={2} /> : <FaRegHandshake />}
        </CustomButton10>
      );
    }
  
    // Handle ride request responses
    if (ride.status === RideStatusV2.REQUEST_SENT) {
      return isPostCreator ? (
        <div className="RAC-action-buttons-v2">
          <CustomButton10Co 
            onClick={() => handleRequestResponse(true)}
            disabled={actionLoading}
          >
            {actionLoading ? <Spinner size="small" showText={false} thickness={2} /> : t('buttons.accept')}
          </CustomButton10Co>
          <CustomButton10Ca 
            onClick={() => handleRequestResponse(false)}
            disabled={actionLoading}
          >
            {actionLoading ? <Spinner size="small" showText={false} thickness={2} /> : t('buttons.decline')}
          </CustomButton10Ca>
        </div>
      ) : (
        <div style={{ fontSize: '11px', textAlign: 'center' }}>{t('ride_actions.request_sent')}</div>
      );
    }

    // Render phase-specific components
    return renderPhaseComponent();
  };

  // Determine if we need to show the action indicator
  const showActionIndicator = !loading && ride && getStatusInfo(ride.status).needsAction;
  
  // Get status info for display, handling loading state
  const getDisplayStatusInfo = () => {
    if (loading) {
      return {
        label: t('app.loading'),
        icon: <FaClock />,
        color: 'var(--accent-color2)',
        needsAction: false
      };
    }
    
    if (ride) {
      return getStatusInfo(ride.status);
    }
    
    return {
      label: userRole || t('posts.ride'),
      icon: <FaCarSide />,
      color: 'var(--main-color)',
      needsAction: false
    };
  };
  
  const statusInfo = getDisplayStatusInfo();

  return (
    <>
      <div className="RAC-ride-actions-v2">
        <div className='RAC-ride-actions-button-fr'>
        <button 
          className={`RAC-ride-actions-button ${showActionIndicator ? 'RAC-needs-action' : ''}`}
          onClick={handleToggleDropdown}
          aria-label={t('ride_actions.toggle_actions')}
          aria-expanded={dropdownOpen}
          aria-haspopup="true"
        >
          <span className="RAC-ride-status-icon" style={{ color: statusInfo.color }}>
            {statusInfo.icon}
          </span>
          <span className="RAC-ride-status-text" style={{ color: statusInfo.color }}>
            {getCompactLabel()}
          </span>
          <FaCaretDown size={14} className="RAC-dropdown-icon" />
          {showActionIndicator && <div className="RAC-action-indicator"></div>}
        </button>
        </div>
        
        {dropdownOpen && (
          <div className="RAC-ride-actions-dropdown" ref={dropdownRef}>
            <div className="RAC-dropdown-header-v2">
              {loading ? (
                <div className="RAC-loading-status">
                  <Spinner size="small" showText={false} />
                  <span>{t('app.loading')}</span>
                </div>
              ) : (
                <StatusDisplay 
                  ride={ride}
                  currentUser={currentUser}
                  otherUser={otherUser}
                  userRole={userRole}
                  otherUserRole={otherUserRole}
                />
              )}
            </div>
            <div className="RAC-dropdown-content-v2">
              {renderActions()}
            </div>
          </div>
        )}
      </div>
      
      {showReviewDialog && ReactDOM.createPortal(
        <RideReviewDialog 
          rideId={ride.id}
          ride={ride}
          isDriver={currentUser?.id === ride.driver_id}
          onClose={() => setShowReviewDialog(false)}
          onReviewSubmitted={(isDriver) => {
            if (isDriver) {
              setDriverReviewSubmitted(true);
            } else {
              setPassengerReviewSubmitted(true);
            }
            // Add system message about review submission
            addSystemMessage(t('ride_actions.review_submitted_message'));
          }}
        />,
        document.body
      )}
    </>
  );
};

// RideReviewDialog Component
const RideReviewDialog = ({ 
  rideId, 
  ride,
  isDriver,
  onClose,
  onReviewSubmitted
}) => {
  const { t } = useTranslation('common');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { addToast } = useToast();

  const handleSubmitReview = async () => {
    if (!rating) {
      setError(t('ride_actions.please_select_rating'));
      return;
    }
    
    if (submitting) return;

    try {
      setSubmitting(true);
      setError(null);

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw new Error(t('ride_actions.authentication_error'));
      if (!user) throw new Error(t('ride_actions.not_authenticated'));

      // Determine the correct reviewee based on the user's role
      const actualRevieweeId = user.id === ride.driver_id ? ride.passenger_id : ride.driver_id;

      // Verify we're not trying to review ourselves
      if (user.id === actualRevieweeId) {
        throw new Error(t('ride_actions.cannot_review_yourself'));
      }

      // Submit the review
      const { data: reviewData, error: reviewError } = await supabase
        .from('reviews')
        .insert({
          ride_id: rideId,
          reviewer_id: user.id,
          reviewee_id: actualRevieweeId,
          rating: parseInt(rating),
          comment: comment?.trim() || null,
          created_at: new Date().toISOString()
        })
        .select('*')
        .single();

      if (reviewError) {
        console.error('Review submission error:', reviewError);
        throw new Error(reviewError.message);
      }

      // Get reviewer's profile for notification
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Error fetching reviewer profile:', profileError);
      }

      const reviewerName = profileData?.username || t('ride_actions.user');

      // Create notification for reviewee
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
          user_id: actualRevieweeId,
          type: 'new_ride_review',
          message: t('reviews.notification_new_review', { name: reviewerName, rating: rating }),
          related_id: reviewData.id,
          sender_id: user.id,
          created_at: new Date().toISOString()
        });

      if (notificationError) {
        console.error('Notification error:', notificationError);
      }

      // Show success toast
      addToast({
        type: 'success',
        title: t('ride_actions.review_submitted'),
        description: t('ride_actions.review_submitted_description')
      });

      // Manual stats update for review (backup)
      try {
        console.log('Updating stats after review submission');
        
        // Update the reviewee's stats to include the new review
        const { error: revieweeStatsError } = await supabase
          .rpc('sync_user_stats_for_user', { p_user_id: actualRevieweeId });
        
        if (revieweeStatsError) {
          console.error('Error updating reviewee stats after review:', revieweeStatsError);
        }
        
      } catch (statsError) {
        console.error('Error in manual review stats update:', statsError);
      }

      onReviewSubmitted(isDriver);
      onClose();

    } catch (error) {
      console.error('Error submitting review:', error);
      setError(error.message || t('ride_actions.failed_to_submit_review'));

      // Show error toast
      addToast({
        type: 'error',
        title: t('ride_actions.error'),
        description: error.message || t('ride_actions.could_not_submit_review')
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Render stars for rating
  const renderStars = () => (
    <div className="RAC-star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={`RAC-star ${star <= rating ? 'RAC-selected' : ''}`}
          onClick={() => setRating(star)}
          onMouseEnter={() => setRating(star)}
          style={{ cursor: 'pointer' }}
          aria-label={t('reviews.star_rating', { count: star })}
          role="button"
          tabIndex={0}
        >
          <FaRegStar />
        </span>
      ))}
    </div>
  );

  return (
    <div className="RAC-modal-overlay" onClick={submitting ? null : onClose}>
      <div className="RAC-modal2" onClick={(e) => e.stopPropagation()}>
        <h2>{t('ride_actions.review_ride')}</h2>
        {error && (
          <div className="RAC-error-message" style={{ color: 'var(--accent-color2)', marginBottom: '10px' }}>
            {error}
          </div>
        )}

        <div className="RAC-review-content">
          {renderStars()}
          
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('ride_actions.share_your_experience')}
            className="RAC-review-textarea"
            rows={4}
            maxLength={500}
            disabled={submitting}
            aria-label={t('reviews.textarea_label')}
          />
        </div>

        <div className="RAC-modal-buttons">
          <CustomButton10Ca
            onClick={onClose}
            label={t('buttons.cancel')}
            disabled={submitting}
          />
          <CustomButton10Co
            onClick={handleSubmitReview}
            label={submitting ? t('buttons.submitting') : t('buttons.submit')}
            disabled={submitting}
          >
            {submitting && <Spinner size="small" showText={false} thickness={2} />}
          </CustomButton10Co>
        </div>
      </div>
    </div>
  );
};

export default RideActionsV2;